import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { searchRooms, reserveRoom, applyVoucher, getMaxCapacity } from '../api';
import RoomList from './RoomList';
import VoucherApplication from './VoucherApplication';
import MandatoryDiscountsManager from './MandatoryDiscountsManager';
import { MANDATORY_DISCOUNT_TYPES, calculateMandatoryDiscount } from '../enums/mandatory_discounts';
import { getPaymentSettings } from '../api/paymentSettings';
import valuesFallback from '../values.json';

// Minimum booking date - can be easily updated
const MIN_BOOKING_DATE = '2025-09-18';

const RoomSearch = () => {
    const [adults, setAdults] = useState(1);
    const [kids, setKids] = useState(0);
    const [checkInDate, setCheckInDate] = useState('');
    const [nights, setNights] = useState(1);
    const [rooms, setRooms] = useState([]);
    const [showDialog, setShowDialog] = useState(false);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [guestName, setGuestName] = useState('');
    const [guestEmail, setGuestEmail] = useState('');
    const [guestContact, setGuestContact] = useState('');
    const [customerNotes, setCustomerNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);
    const [bookingNo, setBookingNo] = useState('');
    const [reservationSummary, setReservationSummary] = useState(null);
    const [toast, setToast] = useState({ visible: false, message: '' });
    const [appliedVoucher, setAppliedVoucher] = useState(null);
    const [mandatoryDiscounts, setMandatoryDiscounts] = useState([]); // Updated to support multiple discounts
    const [agreeToPolicies, setAgreeToPolicies] = useState(false);
    const [showPoliciesDialog, setShowPoliciesDialog] = useState(false);
    const [agreeToDeposit, setAgreeToDeposit] = useState(false);
    const [agreeToCharges, setAgreeToCharges] = useState(false);
    const [maxCapacity, setMaxCapacity] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    
    // Mobile wizard state
    const [isMobileWizard, setIsMobileWizard] = useState(false);
    const [wizardStep, setWizardStep] = useState(1); // 1: Room details, 2: Guest info, 3: Confirmation
    const [showMobileResults, setShowMobileResults] = useState(false);
    
    // Privacy notice state
    const [showPrivacyNotice, setShowPrivacyNotice] = useState(true);
    const [userIpAddress, setUserIpAddress] = useState('');
    const [paymentSettings, setPaymentSettings] = useState({
        adminEmail: valuesFallback.contact.adminEmail,
        paymentChannels: valuesFallback.paymentChannels
    });

    // Load payment settings for thank-you screen (live from API)
    useEffect(() => {
        const loadPaymentSettings = async () => {
            try {
                const data = await getPaymentSettings();
                setPaymentSettings({
                    adminEmail: data.adminEmail || valuesFallback.contact.adminEmail,
                    paymentChannels: Array.isArray(data.paymentChannels) && data.paymentChannels.length > 0
                        ? data.paymentChannels
                        : valuesFallback.paymentChannels
                });
            } catch (error) {
                console.error('Failed to load payment settings, using fallback:', error);
            }
        };
        loadPaymentSettings();
    }, []);

    // Fetch user IP address on component mount
    useEffect(() => {
        const fetchIpAddress = async () => {
            try {
                const response = await fetch('https://api.ipify.org?format=json');
                const data = await response.json();
                setUserIpAddress(data.ip);
            } catch (error) {
                console.error('Error fetching IP address:', error);
                setUserIpAddress('unknown');
            }
        };
        fetchIpAddress();
    }, []);

    // Reset search results when search parameters change
    const resetSearch = () => {
        setRooms([]);
        setHasSearched(false);
    };

    // Fetch max capacity on component mount
    useEffect(() => {
        const fetchMaxCapacity = async () => {
            try {
                const data = await getMaxCapacity();
                setMaxCapacity(data.max_capacity);
            } catch (error) {
                console.error('Error fetching max capacity:', error);
                setMaxCapacity(4); // Fallback to reasonable default
            }
        };
        fetchMaxCapacity();
    }, []);

    // Helper function to check if any mandatory discounts are applied
    const hasAnyMandatoryDiscount = () => {
        return mandatoryDiscounts && mandatoryDiscounts.length > 0;
    };

    // Helper function to get total mandatory discount amount
    const getTotalMandatoryDiscountAmount = (totalRoomRate) => {
        if (!mandatoryDiscounts || mandatoryDiscounts.length === 0) return 0;
        
        return mandatoryDiscounts.reduce((total, discount) => {
            const calculation = calculateMandatoryDiscount(totalRoomRate, discount.type, selectedRoom?.max_capacity);
            return total + calculation.discountAmount;
        }, 0);
    };

    // Helper function to get total VAT exempt amount
    const getTotalVatExemptAmount = (totalRoomRate) => {
        if (!mandatoryDiscounts || mandatoryDiscounts.length === 0) return 0;
        
        return mandatoryDiscounts.reduce((total, discount) => {
            const calculation = calculateMandatoryDiscount(totalRoomRate, discount.type, selectedRoom?.max_capacity);
            return total + calculation.vatExemptAmount;
        }, 0);
    };

    // Calculate check-out date based on check-in date and nights
    const getCheckOutDate = () => {
        if (!checkInDate || !nights) return '';
        // Parse date string manually to avoid timezone conversion
        const parts = checkInDate.split('-');
        const checkIn = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        const checkOut = new Date(checkIn);
        checkOut.setDate(checkIn.getDate() + parseInt(nights));
        const year = checkOut.getFullYear();
        const month = String(checkOut.getMonth() + 1).padStart(2, '0');
        const day = String(checkOut.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const checkOutDate = getCheckOutDate();

    const showToast = (message) => {
        setToast({ visible: true, message });
        setTimeout(() => setToast({ visible: false, message: '' }), 3500);
    };

    const handleVoucherApplied = (voucher) => {
        setAppliedVoucher(voucher);
        // Reset mandatory discounts when voucher is applied (business rule: can't mix BOOKING vouchers with SC/PWD)
        setMandatoryDiscounts([]);
    };

    const handleVoucherRemoved = () => {
        setAppliedVoucher(null);
        // Mandatory discounts can be re-enabled when voucher is removed
    };

    // Helper function to calculate mandatory discount breakdown for multiple discounts
    const getMandatoryDiscountBreakdown = () => {
        if (!selectedRoom || mandatoryDiscounts.length === 0) {
            return null;
        }

        // Parse dates manually to avoid timezone conversion
        const checkInParts = checkInDate.split('-');
        const checkOutParts = checkOutDate.split('-');
        const checkInObj = new Date(parseInt(checkInParts[0]), parseInt(checkInParts[1]) - 1, parseInt(checkInParts[2]));
        const checkOutObj = new Date(parseInt(checkOutParts[0]), parseInt(checkOutParts[1]) - 1, parseInt(checkOutParts[2]));
        const daysCount = Math.ceil((checkOutObj - checkInObj) / (1000 * 60 * 60 * 24));
        const totalRoomRate = parseFloat(selectedRoom.rate_per_night || selectedRoom.price_per_night) * daysCount;
        
        // Calculate total savings from all discounts using RA 9994 formula
        let totalDiscountAmount = 0;
        let totalVatExemptAmount = 0;
        
        mandatoryDiscounts.forEach(discount => {
            // Use the correct RA 9994 calculation
            const calculation = calculateMandatoryDiscount(totalRoomRate, discount.type, selectedRoom.max_capacity);
            totalDiscountAmount += calculation.discountAmount;
            totalVatExemptAmount += calculation.vatExemptAmount;
        });
        
        const totalSavings = totalDiscountAmount + totalVatExemptAmount;
        const finalAmount = totalRoomRate - totalSavings;

        return {
            baseAmount: totalRoomRate,
            discountAmount: totalDiscountAmount,
            vatExemptAmount: totalVatExemptAmount,
            finalAmount: Math.max(0, finalAmount),
            totalSavings,
            discountCount: mandatoryDiscounts.length
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!adults || !checkInDate || !nights) {
            showToast('Please fill in all required fields.');
            return;
        }

        // Validate check-in date is not before October 18, 2025
        if (checkInDate < MIN_BOOKING_DATE) {
            showToast('Bookings are available starting from October 18, 2025.');
            return;
        }

        const searchParams = {
            adults,
            kids,
            check_in_date: checkInDate,
            check_out_date: checkOutDate,
        };
        
        console.log('Searching with params:', searchParams);
        
        try {
            const results = await searchRooms(searchParams);
            console.log('Search results received:', results.map(r => ({ 
              name: r.name, 
              available_rooms: r.available_rooms, 
              is_day_restricted: r.is_day_restricted 
            })));
            setRooms(results);
            setHasSearched(true);
            
            // Auto-show results dialog on mobile if rooms found
            if (window.innerWidth <= 768 && results.length > 0) {
                setTimeout(() => setShowMobileResults(true), 300); // Small delay for better UX
            }
        } catch (error) {
            showToast('Error searching rooms. Please check your information and try again.');
            console.error('Error searching rooms:', error);
            setHasSearched(true);
        }
    };

    const handleReserve = (room) => {
        // Close mobile results dialog if open
        if (showMobileResults) {
            setShowMobileResults(false);
        }
        
        // Check if mobile view
        if (window.innerWidth <= 768) {
            startMobileWizard(room);
        } else {
            // Desktop behavior - regular dialog
            setSelectedRoom(room);
            setShowDialog(true);
            setGuestName('');
            setGuestEmail('');
            setGuestContact('');
            setCustomerNotes('');
            setAppliedVoucher(null);
            setMandatoryDiscounts([]);
            setAgreeToPolicies(false);
            setShowPoliciesDialog(false);
            setAgreeToDeposit(false);
            setAgreeToCharges(false);
        }
    };

    const handleDialogCancel = () => {
        setShowDialog(false);
        setSelectedRoom(null);
        setMandatoryDiscounts([]); // Reset to empty array for multiple discounts
        setAgreeToPolicies(false);
        setShowPoliciesDialog(false);
        setAgreeToDeposit(false);
        setAgreeToCharges(false);
        setIsMobileWizard(false);
        setWizardStep(1);
        setShowMobileResults(false);
    };

    // Mobile wizard navigation functions
    const startMobileWizard = (room) => {
        setSelectedRoom(room);
        setIsMobileWizard(true);
        setWizardStep(1);
        setShowDialog(true);
        setGuestName('');
        setGuestEmail('');
        setGuestContact('');
        setCustomerNotes('');
        setAppliedVoucher(null);
        setMandatoryDiscounts([]);
        setAgreeToPolicies(false);
        setShowPoliciesDialog(false);
        setAgreeToDeposit(false);
        setAgreeToCharges(false);
    };

    const nextWizardStep = () => {
        setWizardStep(prev => Math.min(prev + 1, 3));
    };

    const previousWizardStep = () => {
        setWizardStep(prev => Math.max(prev - 1, 1));
    };

    const exitWizard = () => {
        setIsMobileWizard(false);
        setWizardStep(1);
        setShowDialog(false);
        setSelectedRoom(null);
        setShowMobileResults(false);
    };

    const handleDisabledButtonClick = () => {
        // Check which required field is missing and provide visual feedback
        if (!guestName.trim()) {
            showToast('Please enter the guest name to proceed.');
            // Scroll to guest name field
            setTimeout(() => {
                const guestNameInput = document.querySelector('input[value="' + guestName + '"]');
                if (guestNameInput) {
                    guestNameInput.focus();
                    guestNameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return;
        }
        
        if (!guestEmail.trim()) {
            showToast('Please enter the guest email to proceed.');
            // Scroll to email field
            setTimeout(() => {
                const emailInput = document.querySelector('input[type="email"]');
                if (emailInput) {
                    emailInput.focus();
                    emailInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return;
        }
        
        if (!guestContact.trim()) {
            showToast('Please enter the guest contact number to proceed.');
            // Scroll to contact field
            setTimeout(() => {
                const contactInputs = document.querySelectorAll('input[type="text"]');
                // Find the contact input (typically the last text input in the form)
                const contactInput = Array.from(contactInputs).find(input => 
                    input.value === guestContact || input.placeholder?.includes('contact') || input.previousElementSibling?.textContent?.includes('Contact')
                );
                if (contactInput) {
                    contactInput.focus();
                    contactInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return;
        }
        
        if (!agreeToPolicies) {
            showToast('Please agree to the Resort Policies to proceed.');
            // Scroll to policies checkbox
            setTimeout(() => {
                const policiesCheckbox = document.querySelector('input[type="checkbox"]');
                if (policiesCheckbox) {
                    policiesCheckbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return;
        }
        
        if (!agreeToDeposit) {
            showToast('Please acknowledge the deposit requirement to proceed.');
            // Scroll to deposit checkbox
            setTimeout(() => {
                const checkboxes = document.querySelectorAll('input[type="checkbox"]');
                if (checkboxes.length > 1) {
                    checkboxes[1].scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return;
        }
        
        if (!agreeToCharges) {
            showToast('Please acknowledge the additional charges policy to proceed.');
            // Scroll to charges checkbox
            setTimeout(() => {
                const checkboxes = document.querySelectorAll('input[type="checkbox"]');
                if (checkboxes.length > 2) {
                    checkboxes[2].scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return;
        }
    };

    const handleDialogProceed = async () => {
        if (!guestName.trim()) {
            showToast('Please enter the guest name.');
            return;
        }

        if (!guestEmail.trim()) {
            showToast('Please enter the guest email.');
            return;
        }

        if (!guestContact.trim()) {
            showToast('Please enter the guest contact number.');
            return;
        }

        // Validate mandatory discounts
        for (const discount of mandatoryDiscounts) {
            if (!discount.id_number || !discount.guest_name) {
                showToast('Please complete all mandatory discount information.');
                return;
            }
        }

        // Cap SC/PWD discounts at room max capacity
        const roomMaxCapacity = selectedRoom?.max_capacity != null
            ? parseInt(selectedRoom.max_capacity, 10)
            : null;
        if (roomMaxCapacity != null && !Number.isNaN(roomMaxCapacity)
            && mandatoryDiscounts.length > roomMaxCapacity) {
            showToast(`Cannot apply more SC/PWD discounts than the room's maximum capacity (${roomMaxCapacity}).`);
            return;
        }

        // Validate policies agreement
        if (!agreeToPolicies) {
            showToast('Please agree to the Resort Policies to proceed.');
            return;
        }

        // Validate deposit agreement
        if (!agreeToDeposit) {
            showToast('Please acknowledge the deposit requirement to proceed.');
            return;
        }

        // Validate additional charges agreement
        if (!agreeToCharges) {
            showToast('Please acknowledge the additional charges policy to proceed.');
            return;
        }

        setIsSubmitting(true);
        try {
            const reservationDetails = {
                room_type_id: selectedRoom.id,
                guest_name: guestName,
                guest_email: guestEmail,
                guest_contact: guestContact,
                check_in_date: checkInDate,
                check_out_date: checkOutDate,
                adults,
                kids,
                customer_notes: customerNotes,
                mandatory_discounts: mandatoryDiscounts, // Send array of discounts
                ip_address: userIpAddress, // Include user's IP address
                price_per_night: selectedRoom.price_per_night // Send the seasonal price from search results
            };
            const result = await reserveRoom(reservationDetails);
            
            // Apply voucher if one is selected
            if (appliedVoucher && appliedVoucher.discountAmount > 0) {
                try {
                    await applyVoucher(result.id, appliedVoucher.code, appliedVoucher.discountAmount);
                    console.log('Voucher applied successfully to reservation:', result.id);
                } catch (voucherError) {
                    console.error('Failed to apply voucher:', voucherError);
                    // Continue with reservation even if voucher application fails
                    showToast('Reservation created but voucher could not be applied. Please contact support.');
                }
            }
            
            setShowDialog(false);
            setSelectedRoom(null);

            // Assume result.booking_no and result.total_amount are returned
            setBookingNo(result.booking_no || 'N/A');
            
            // Calculate discount breakdown for confirmation dialog
            const baseAmount = getBaseAmount();
            const mandatoryBreakdown = getMandatoryDiscountBreakdown();
            
            setReservationSummary({
                pax: parseInt(adults) + parseInt(kids),
                room: selectedRoom.name,
                nights: getNights(),
                dates: `${checkInDate} to ${checkOutDate}`,
                total: getTotalAmount(), // Final amount after all discounts
                baseAmount: baseAmount,
                mandatoryDiscounts: mandatoryDiscounts, // Updated to pass the full discount array
                mandatoryDiscountAmount: mandatoryBreakdown?.discountAmount || 0,
                vatExemptAmount: mandatoryBreakdown?.vatExemptAmount || 0,
                voucherDiscountAmount: appliedVoucher?.discountAmount || 0,
                voucherCode: appliedVoucher?.code || null
            });
            setShowThankYou(true);
        } catch (error) {
            alert('Failed to reserve room.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getNights = () => {
        if (!checkInDate || !checkOutDate) return 1;
        // Parse dates manually to avoid timezone conversion
        const checkInParts = checkInDate.split('-');
        const checkOutParts = checkOutDate.split('-');
        const checkInObj = new Date(parseInt(checkInParts[0]), parseInt(checkInParts[1]) - 1, parseInt(checkInParts[2]));
        const checkOutObj = new Date(parseInt(checkOutParts[0]), parseInt(checkOutParts[1]) - 1, parseInt(checkOutParts[2]));
        const nights = (checkOutObj - checkInObj) / (1000 * 60 * 60 * 24);
        return nights > 0 ? nights : 1;
    };

    // Calculate total amount with voucher and mandatory discounts
    const getTotalAmount = () => {
        if (!selectedRoom || !checkInDate || !checkOutDate) return 0;
        // Parse dates manually to avoid timezone conversion
        const checkInParts = checkInDate.split('-');
        const checkOutParts = checkOutDate.split('-');
        const checkInObj = new Date(parseInt(checkInParts[0]), parseInt(checkInParts[1]) - 1, parseInt(checkInParts[2]));
        const checkOutObj = new Date(parseInt(checkOutParts[0]), parseInt(checkOutParts[1]) - 1, parseInt(checkOutParts[2]));
        const nights = (checkOutObj - checkInObj) / (1000 * 60 * 60 * 24);
        const totalRoomRate = selectedRoom.price_per_night * (nights > 0 ? nights : 1);
        
        // Apply mandatory discounts first
        const totalMandatoryDiscountAmount = getTotalMandatoryDiscountAmount(totalRoomRate);
        const totalVatExemptAmount = getTotalVatExemptAmount(totalRoomRate);
        let finalAmount = totalRoomRate - totalMandatoryDiscountAmount - totalVatExemptAmount;
        
        // Then apply voucher discount if applicable
        const voucherDiscount = appliedVoucher?.discountAmount || 0;
        finalAmount = Math.max(0, finalAmount - voucherDiscount);
        
        return finalAmount;
    };

    const getBaseAmount = () => {
        if (!selectedRoom || !checkInDate || !checkOutDate) return 0;
        // Parse dates manually to avoid timezone conversion
        const checkInParts = checkInDate.split('-');
        const checkOutParts = checkOutDate.split('-');
        const checkInObj = new Date(parseInt(checkInParts[0]), parseInt(checkInParts[1]) - 1, parseInt(checkInParts[2]));
        const checkOutObj = new Date(parseInt(checkOutParts[0]), parseInt(checkOutParts[1]) - 1, parseInt(checkOutParts[2]));
        const nights = (checkOutObj - checkInObj) / (1000 * 60 * 60 * 24);
        return selectedRoom.price_per_night * (nights > 0 ? nights : 1);
    };

    const renderNoRoomsMessage = () => {
        if (!hasSearched || rooms.length > 0) return null;

        const adultsCount = parseInt(adults) || 1;
        const exceedsCapacity = maxCapacity && adultsCount > maxCapacity;

        if (exceedsCapacity) {
            return (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '20px',
                    color: '#2a3b5c',
                    lineHeight: '1.6',
                    marginTop: '20px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px'
                }}>
                    <p>
                        <strong>No Rooms Found</strong><br />
                        We couldn't find any rooms that can accommodate the number of adults you entered. 
                        Our largest room type has reached its maximum capacity.<br /><br />
                        👉 Try reducing the number of adults in your search and consider booking multiple rooms to fit your group.
                    </p>
                </div>
            );
        } else {
            return (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '20px',
                    color: '#2a3b5c',
                    lineHeight: '1.6',
                    marginTop: '20px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px'
                }}>
                    <p>
                        <strong>No Rooms Available</strong><br />
                        No rooms are available for the selected dates. Please try different dates or adjust your search.
                    </p>
                </div>
            );
        }
    };

    return (
        <>
            {/* Privacy Notice Banner */}
            {showPrivacyNotice && createPortal(
                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    width: '100%',
                    background: 'linear-gradient(135deg, #2a3b5c 0%, #3d5270 100%)',
                    color: '#fff',
                    padding: window.innerWidth <= 768 ? '16px 20px' : '20px 40px',
                    boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
                    zIndex: 5000,
                    fontFamily: 'Segoe UI, Arial, sans-serif',
                    fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                    lineHeight: '1.6',
                    borderTop: '3px solid #4f8cff',
                    boxSizing: 'border-box'
                }}>
                    <div style={{ 
                        maxWidth: '1200px', 
                        margin: '0 auto',
                        display: 'flex',
                        flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                        alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
                        gap: window.innerWidth <= 768 ? '12px' : '20px'
                    }}>
                        <div style={{ flex: 1 }}>
                            <strong style={{ display: 'block', marginBottom: '8px', fontSize: window.innerWidth <= 768 ? '15px' : '16px', color: '#6ec6ff' }}>
                                Privacy & Data Collection Notice
                            </strong>
                            <p style={{ margin: 0 }}>
                                By using this booking system, you agree that La Casita Beach Resort may collect and use your personal information, including but not limited to your name, email, contact details, and technical data (such as IP address and browser information), solely for processing your reservation and enhancing your experience. We value your privacy and will never sell or share your data with third parties for marketing purposes.
                            </p>
                        </div>
                        <button
                            onClick={() => setShowPrivacyNotice(false)}
                            style={{
                                background: '#4f8cff',
                                color: '#fff',
                                border: 'none',
                                padding: window.innerWidth <= 768 ? '10px 20px' : '12px 28px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 2px 8px rgba(79, 140, 255, 0.3)',
                                transition: 'all 0.3s ease',
                                alignSelf: window.innerWidth <= 768 ? 'stretch' : 'center'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#357ae8';
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(79, 140, 255, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = '#4f8cff';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 8px rgba(79, 140, 255, 0.3)';
                            }}
                        >
                            I Understand
                        </button>
                    </div>
                </div>,
                document.body
            )}

            {toast.visible && (
                <div style={{
                    position: 'fixed',
                    top: 24,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#ff4d4f',
                    color: '#fff',
                    padding: '12px 32px',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(44,62,80,0.13)',
                    zIndex: 4000,
                    fontWeight: 600,
                    fontSize: '1.05em',
                    minWidth: 260,
                    textAlign: 'center',
                    animation: 'slideDown 0.4s',
                }}>
                    {toast.message}
                </div>
            )}
            <form onSubmit={handleSubmit}>
                <div style={{ 
                    display: 'flex', 
                    flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                    gap: window.innerWidth <= 768 ? 12 : 16, 
                    marginBottom: 12 
                }}>
                    <div className="form-group" style={{ flex: 1, margin: 0 }}>
                        <label>Adults:</label>
                        <input
                            type="number"
                            min="1"
                            value={adults}
                            onChange={e => {
                                setAdults(e.target.value);
                                resetSearch();
                            }}
                            style={window.innerWidth <= 768 ? { 
                                padding: '12px 16px', 
                                fontSize: '16px',
                                borderRadius: '6px',
                                width: '100%',
                                boxSizing: 'border-box',
                                maxWidth: '100%'
                            } : {}}
                        />
                    </div>
                    <div className="form-group" style={{ flex: 1, margin: 0 }}>
                        <label>Kids (Below 3ft):</label>
                        <input
                            type="number"
                            min="0"
                            value={kids}
                            onChange={e => {
                                setKids(e.target.value);
                                resetSearch();
                            }}
                            style={window.innerWidth <= 768 ? { 
                                padding: '12px 16px', 
                                fontSize: '16px',
                                borderRadius: '6px',
                                width: '100%',
                                boxSizing: 'border-box',
                                maxWidth: '100%'
                            } : {}}
                        />
                    </div>
                </div>
                <div style={{ 
                    display: 'flex', 
                    flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                    gap: window.innerWidth <= 768 ? 12 : 16, 
                    marginBottom: 12 
                }}>
                    <div className="form-group" style={{ flex: window.innerWidth <= 768 ? 1 : 2, margin: 0 }}>
                        <label>Check-in Date:</label>
                        <input
                            type="date"
                            min="2025-09-18"
                            value={checkInDate}
                            onChange={(e) => {
                                setCheckInDate(e.target.value);
                                resetSearch();
                            }}
                            required
                            style={window.innerWidth <= 768 ? { 
                                padding: '12px 16px', 
                                fontSize: '16px',
                                borderRadius: '6px',
                                width: '100%',
                                boxSizing: 'border-box',
                                maxWidth: '100%'
                            } : {}}
                        />
                    </div>
                    <div className="form-group" style={{ flex: 1, margin: 0 }}>
                        <label>Duration:</label>
                        <select
                            value={nights}
                            onChange={(e) => {
                                setNights(e.target.value);
                                resetSearch();
                            }}
                            required
                            style={window.innerWidth <= 768 ? { 
                                padding: '12px 16px', 
                                fontSize: '16px',
                                borderRadius: '6px',
                                width: '100%',
                                boxSizing: 'border-box',
                                maxWidth: '100%'
                            } : {}}
                        >
                            <option value="1">1 night</option>
                            <option value="2">2 nights</option>
                            <option value="3">3 nights</option>
                            <option value="4">4 nights</option>
                            <option value="5">5 nights</option>
                            <option value="6">6 nights</option>
                            <option value="7">1 week</option>
                            <option value="14">2 weeks</option>
                            <option value="21">3 weeks</option>
                            <option value="30">1 month</option>
                        </select>
                    </div>
                </div>
                {checkOutDate && (
                    <div style={{ 
                        marginBottom: 18, 
                        padding: '12px 16px', 
                        background: '#f7fbff', 
                        border: '1.5px solid #b6d0ff', 
                        borderRadius: '8px',
                        fontSize: '0.95em',
                        color: '#2a3b5c'
                    }}>
                        <strong>Check-out:</strong> {checkOutDate} ({nights} night{nights > 1 ? 's' : ''})
                    </div>
                )}
                <button type="submit">Search Rooms</button>
            </form>
            {/* Desktop: Show results inline */}
            {rooms.length > 0 && window.innerWidth > 768 && (
                <RoomList rooms={rooms} onReserve={handleReserve} />
            )}
            {/* Mobile: Show results button */}
            {rooms.length > 0 && window.innerWidth <= 768 && (
                <div style={{ marginTop: 16 }}>
                    <button
                        onClick={() => setShowMobileResults(true)}
                        style={{
                            width: '100%',
                            padding: '14px 20px',
                            background: 'linear-gradient(90deg, #4f8cff 0%, #6ec6ff 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: '16px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span>View Available Rooms ({rooms.length})</span>
                        <span>→</span>
                    </button>
                </div>
            )}
            {renderNoRoomsMessage()}

            {/* Desktop Dialog - Regular flow */}
            {showDialog && selectedRoom && !isMobileWizard && createPortal(
                <div style={{
                    position: 'fixed', 
                    top: 0, 
                    left: 0, 
                    width: '100vw', 
                    height: '100vh',
                    background: 'rgba(0,0,0,0.6)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    zIndex: 9999,
                    margin: 0,
                    padding: '16px',
                    boxSizing: 'border-box'
                }}>
                    <div style={{ 
                        background: '#fff', 
                        padding: '28px 24px', 
                        borderRadius: 12, 
                        minWidth: 320, 
                        maxWidth: 500,
                        width: '90vw',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 8px 32px rgba(44,62,80,0.15)',
                        fontFamily: 'Segoe UI, Arial, sans-serif',
                        position: 'relative'
                    }}>
                        <h2 style={{ 
                            color: '#4f8cff', 
                            margin: '0 0 16px 0',
                            borderBottom: '2px solid #e3f0ff',
                            paddingBottom: '8px'
                        }}>
                            Reserve: {selectedRoom.name}
                        </h2>
                        
                        {/* Booking Summary */}
                        <div style={{
                            background: '#f7fbff',
                            border: '1.5px solid #e3f0ff',
                            borderRadius: 8,
                            padding: 16,
                            marginBottom: 16
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.95em' }}>
                                <div><strong>Adults:</strong> {adults}</div>
                                <div><strong>Kids:</strong> {kids}</div>
                                <div><strong>Check-in:</strong> {checkInDate}</div>
                                <div><strong>Check-out:</strong> {checkOutDate}</div>
                            </div>
                            <div style={{ marginTop: 12, padding: '12px 0', borderTop: '1px solid #e3f0ff' }}>
                                <h4 style={{ margin: '0 0 8px 0', color: '#2a3b5c', fontSize: '1.05em' }}>
                                    💰 Cost Summary
                                </h4>
                                
                                {/* Mutual Exclusivity Notice */}
                                {(hasAnyMandatoryDiscount() || appliedVoucher) && (
                                    <div style={{
                                        background: '#e7f3ff',
                                        border: '1px solid #b3d9ff',
                                        borderRadius: 4,
                                        padding: '8px',
                                        marginBottom: 12,
                                        fontSize: '0.85em',
                                        color: '#2a3b5c'
                                    }}>
                                        ℹ️ Note: Only one discount type can be applied per booking (Mandatory Discount OR Voucher).
                                    </div>
                                )}
                                
                                {/* Base Amount */}
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    margin: '4px 0',
                                    fontSize: '0.95em',
                                    color: '#2a3b5c'
                                }}>
                                    <span>Base Amount ({nights} night{nights > 1 ? 's' : ''} × ₱{selectedRoom?.price_per_night?.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}):</span>
                                    <span>₱{getBaseAmount().toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>

                                {/* Mandatory Discount Breakdown */}
                                {hasAnyMandatoryDiscount() && (() => {
                                    const totalRoomRate = getBaseAmount();
                                    const totalDiscountAmount = getTotalMandatoryDiscountAmount(totalRoomRate);
                                    const totalVatExemptAmount = getTotalVatExemptAmount(totalRoomRate);
                                    
                                    return mandatoryDiscounts.map((discount, index) => {
                                        const calculation = calculateMandatoryDiscount(totalRoomRate, discount.type, selectedRoom?.max_capacity);
                                        return (
                                            <div key={index} style={{ marginBottom: '8px', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                                                <div style={{ 
                                                    fontSize: '0.9em',
                                                    fontWeight: 'bold',
                                                    color: '#2a3b5c',
                                                    marginBottom: '4px'
                                                }}>
                                                    {discount.type === MANDATORY_DISCOUNT_TYPES.SENIOR ? 'Senior Citizen' : 'PWD'} - {discount.guestName}
                                                </div>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    margin: '2px 0',
                                                    fontSize: '0.85em',
                                                    color: '#6c757d'
                                                }}>
                                                    <span>Per-person share (₱{totalRoomRate.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ÷ {selectedRoom?.max_capacity}):</span>
                                                    <span>₱{calculation.perPersonShare.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                                </div>
                                                {/* Remove VAT-exempt base display for non-VAT business */}
                                                <div style={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    margin: '2px 0',
                                                    fontSize: '0.95em',
                                                    color: '#dc3545'
                                                }}>
                                                    <span>20% Discount:</span>
                                                    <span>-₱{calculation.discountAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                                </div>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    margin: '4px 0 0 0',
                                                    fontSize: '0.9em',
                                                    fontWeight: 'bold',
                                                    color: '#28a745',
                                                    borderTop: '1px solid #dee2e6',
                                                    paddingTop: '4px'
                                                }}>
                                                    <span>Net amount per person:</span>
                                                    <span>₱{calculation.finalAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}

                                {/* Voucher Discount */}
                                {appliedVoucher && appliedVoucher.discountAmount > 0 && (
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        margin: '4px 0',
                                        fontSize: '0.95em',
                                        color: '#dc3545'
                                    }}>
                                        <span>Voucher Discount ({appliedVoucher.code}):</span>
                                        <span>-₱{appliedVoucher.discountAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                    </div>
                                )}

                                {/* Total Savings */}
                                {(hasAnyMandatoryDiscount() || (appliedVoucher && appliedVoucher.discountAmount > 0)) && (
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        margin: '6px 0',
                                        padding: '6px 0',
                                        borderTop: '1px solid #e9ecef',
                                        fontSize: '0.9em',
                                        color: '#28a745',
                                        fontWeight: 'bold'
                                    }}>
                                        <span>Total Savings:</span>
                                        <span>
                                            ₱{(() => {
                                                let totalSavings = 0;
                                                
                                                // Add mandatory discount savings
                                                if (hasAnyMandatoryDiscount()) {
                                                    const totalRoomRate = getBaseAmount();
                                                    totalSavings += getTotalMandatoryDiscountAmount(totalRoomRate) + getTotalVatExemptAmount(totalRoomRate);
                                                }
                                                
                                                // Add voucher savings
                                                if (appliedVoucher && appliedVoucher.discountAmount > 0) {
                                                    totalSavings += appliedVoucher.discountAmount;
                                                }
                                                
                                                return totalSavings.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
                                            })()}
                                        </span>
                                    </div>
                                )}

                                {/* Final Total */}
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    margin: '8px 0 0 0',
                                    padding: '8px 0',
                                    borderTop: '2px solid #4f8cff',
                                    fontSize: '1.15em', 
                                    fontWeight: 'bold', 
                                    color: '#4f8cff'
                                }}>
                                    <span>Final Total:</span>
                                    <span>₱{getTotalAmount().toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>
                            </div>
                        </div>

                        {/* Voucher Application */}
                        <VoucherApplication 
                            onVoucherApplied={handleVoucherApplied}
                            onVoucherRemoved={handleVoucherRemoved}
                            appliedVoucher={appliedVoucher}
                            bookingTotal={getBaseAmount()}
                            roomRate={selectedRoom.price_per_night}
                            hasAnyMandatoryDiscount={hasAnyMandatoryDiscount()}
                            selectedRoomTypeId={selectedRoom.id}
                            checkInDate={checkInDate}
                        />

                        {/* Mandatory Discounts Section */}
                        <div style={{ 
                            marginTop: 16, 
                            padding: '15px',
                            background: appliedVoucher ? '#f8f9fa' : '#fff9c4',
                            border: appliedVoucher ? '2px solid #dee2e6' : '2px solid #ffc107',
                            borderRadius: 8,
                            opacity: appliedVoucher ? 0.6 : 1,
                            position: 'relative'
                        }}>
                            {appliedVoucher && (
                                <div style={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    background: '#dc3545',
                                    color: '#fff',
                                    padding: '4px 8px',
                                    borderRadius: 4,
                                    fontSize: '0.8em',
                                    fontWeight: 'bold'
                                }}>
                                    DISABLED
                                </div>
                            )}
                            
                            {/* Multi-Discount Support - Updated UI */}
                            <MandatoryDiscountsManager
                                discounts={mandatoryDiscounts}
                                setDiscounts={setMandatoryDiscounts}
                                disabled={!!appliedVoucher}
                                showPreview={true}
                                maxCapacity={selectedRoom?.max_capacity}
                            />
                            
                            {appliedVoucher && (
                                <div style={{
                                    background: '#f8d7da',
                                    border: '1px solid #f5c6cb',
                                    borderRadius: 6,
                                    padding: '12px',
                                    marginTop: 12,
                                    fontSize: '0.9em',
                                    color: '#721c24'
                                }}>
                                    ⚠️ <strong>Mandatory discounts are disabled when a voucher is applied.</strong>
                                    <br />Remove the voucher to enable mandatory discounts.
                                </div>
                            )}

                            {/* Show discount breakdown if applicable */}
                            {mandatoryDiscounts.length > 0 && selectedRoom && !appliedVoucher && (
                                <div style={{ 
                                    background: '#e8f5e8',
                                    border: '1px solid #c3e6c3',
                                    borderRadius: 6,
                                    padding: '12px',
                                    marginTop: 12
                                }}>
                                    <h5 style={{ margin: '0 0 8px 0', color: '#28a745' }}>💰 Discount Breakdown ({mandatoryDiscounts.length} discount{mandatoryDiscounts.length > 1 ? 's' : ''}):</h5>
                                    {(() => {
                                        const breakdown = getMandatoryDiscountBreakdown();
                                        return breakdown ? (
                                            <div style={{ fontSize: '0.9em', color: '#2a3b5c' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0' }}>
                                                    <span>Base Amount:</span>
                                                    <span>₱{breakdown.baseAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0' }}>
                                                    <span>Total 20% Discounts:</span>
                                                    <span style={{ color: '#dc3545' }}>-₱{breakdown.discountAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                                </div>
                                                {/* <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0' }}>
                                                    <span>Total VAT Exemptions (12%):</span>
                                                    <span style={{ color: '#dc3545' }}>-₱{breakdown.vatExemptAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                                </div> */}
                                                <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #c3e6c3' }} />
                                                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0', fontWeight: 'bold', color: '#28a745' }}>
                                                    <span>Discounted Amount:</span>
                                                    <span>₱{breakdown.finalAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0', fontSize: '0.85em', color: '#28a745' }}>
                                                    <span>Total Savings:</span>
                                                    <span>₱{breakdown.totalSavings.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                                </div>
                                                <div style={{ 
                                                    fontSize: '0.8em', 
                                                    color: '#666', 
                                                    marginTop: '8px', 
                                                    fontStyle: 'italic',
                                                    padding: '6px',
                                                    background: '#f8f9fa',
                                                    borderRadius: '3px'
                                                }}>
                                                    Note: Each discount calculated as (Room Charge ÷ Room Capacity) × 20%
                                                </div>
                                            </div>
                                        ) : null;
                                    })()}
                                </div>
                            )}
                        </div>

                        {/* Guest Information Form */}
                        <div style={{ marginTop: 16 }}>
                            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#2a3b5c' }}>
                                Guest Name: <span style={{ color: '#dc3545' }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={guestName}
                                onChange={e => setGuestName(e.target.value)}
                                required
                                style={{
                                    width: '100%',
                                    padding: window.innerWidth <= 768 ? '12px 16px' : '8px 12px',
                                    border: '1px solid #ddd',
                                    borderRadius: 4,
                                    fontSize: window.innerWidth <= 768 ? '16px' : '1rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        
                        <div style={{ marginTop: window.innerWidth <= 768 ? 16 : 12 }}>
                            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#2a3b5c' }}>
                                Email: <span style={{ color: '#dc3545' }}>*</span>
                            </label>
                            <input
                                type="email"
                                value={guestEmail}
                                onChange={e => setGuestEmail(e.target.value)}
                                required
                                style={{
                                    width: '100%',
                                    padding: window.innerWidth <= 768 ? '12px 16px' : '8px 12px',
                                    border: '1px solid #ddd',
                                    borderRadius: 4,
                                    fontSize: window.innerWidth <= 768 ? '16px' : '1rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        
                        <div style={{ marginTop: window.innerWidth <= 768 ? 16 : 12 }}>
                            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#2a3b5c' }}>
                                Mobile/Contact No.: <span style={{ color: '#dc3545' }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={guestContact}
                                onChange={e => setGuestContact(e.target.value)}
                                required
                                style={{
                                    width: '100%',
                                    padding: window.innerWidth <= 768 ? '12px 16px' : '8px 12px',
                                    border: '1px solid #ddd',
                                    borderRadius: 4,
                                    fontSize: window.innerWidth <= 768 ? '16px' : '1rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        
                        <div style={{ marginTop: window.innerWidth <= 768 ? 16 : 12 }}>
                            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#2a3b5c' }}>
                                Customer Notes:
                            </label>
                            <textarea
                                value={customerNotes}
                                onChange={e => setCustomerNotes(e.target.value)}
                                placeholder="Add notes or special requests here..."
                                style={{
                                    width: '100%',
                                    minHeight: window.innerWidth <= 768 ? 80 : 60,
                                    padding: window.innerWidth <= 768 ? '12px 16px' : '8px 12px',
                                    border: '1px solid #ddd',
                                    borderRadius: 4,
                                    fontSize: window.innerWidth <= 768 ? '16px' : '1rem',
                                    resize: 'vertical',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        {/* Cost Breakdown Summary */}
                        <div style={{
                            marginTop: 20,
                            padding: '16px',
                            background: '#f8f9fa',
                            border: '2px solid #4f8cff',
                            borderRadius: 8
                        }}>
                            <h4 style={{ 
                                margin: '0 0 12px 0', 
                                color: '#4f8cff', 
                                fontSize: '1.1em',
                                textAlign: 'center'
                            }}>
                                💳 Payment Summary
                            </h4>
                            
                            <div style={{ fontSize: '1.05em', lineHeight: '1.6' }}>
                                {/* Base Amount */}
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    marginBottom: '8px',
                                    color: '#2a3b5c'
                                }}>
                                    <span>
                                        Base Amount (₱{selectedRoom?.price_per_night?.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} × {getNights()} night{getNights() > 1 ? 's' : ''}):
                                    </span>
                                    <span style={{ fontWeight: 'bold' }}>
                                        ₱{getBaseAmount().toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </span>
                                </div>

                                {/* Discounts */}
                                {(() => {
                                    let discountLabel = '';
                                    let discountAmount = 0;
                                    
                                    if (hasAnyMandatoryDiscount()) {
                                        const breakdown = getMandatoryDiscountBreakdown();
                                        if (breakdown) {
                                            if (breakdown.discountCount === 1) {
                                                discountLabel = mandatoryDiscounts[0].type === MANDATORY_DISCOUNT_TYPES.SENIOR ? 'SC' : 'PWD';
                                            } else {
                                                discountLabel = `${breakdown.discountCount} Discounts`;
                                            }
                                            discountAmount = breakdown.discountAmount + breakdown.vatExemptAmount;
                                        }
                                    } else if (appliedVoucher && appliedVoucher.discountAmount > 0) {
                                        discountLabel = 'Voucher';
                                        discountAmount = appliedVoucher.discountAmount;
                                    }
                                    
                                    return discountAmount > 0 ? (
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            marginBottom: '8px',
                                            color: '#dc3545'
                                        }}>
                                            <span>Discounts ({discountLabel}):</span>
                                            <span style={{ fontWeight: 'bold' }}>
                                                -₱{discountAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                            </span>
                                        </div>
                                    ) : null;
                                })()}

                                {/* Total Amount */}
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    paddingTop: '12px',
                                    borderTop: '2px solid #4f8cff',
                                    fontSize: '1.2em',
                                    fontWeight: 'bold',
                                    color: '#4f8cff'
                                }}>
                                    <span>Total Amount:</span>
                                    <span>₱{getTotalAmount().toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>
                            </div>
                        </div>

                        {/* Payment Information Note */}
                        <div style={{ 
                            marginTop: 16,
                            marginBottom: 16,
                            padding: '16px', 
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px',
                            borderLeft: '4px solid #4f8cff'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '8px'
                            }}>
                                <span style={{
                                    fontSize: '18px',
                                    marginRight: '8px'
                                }}>💡</span>
                                <span style={{
                                    fontWeight: '600',
                                    color: '#2a3b5c',
                                    fontSize: '14px'
                                }}>Payment Information</span>
                            </div>
                            <p style={{
                                margin: 0,
                                color: '#6c757d',
                                fontSize: '13px',
                                lineHeight: '1.5'
                            }}>
                                You may settle the full payment to secure your reservation faster, but at least 50% is required to confirm your booking. The balance can be paid upon check-in.
                            </p>
                        </div>

                        {/* Policies Agreement Section */}
                        <div style={{ 
                            marginTop: 16, 
                            padding: '15px', 
                            border: '1px solid #ddd', 
                            borderRadius: '8px', 
                            backgroundColor: '#f9f9f9' 
                        }}>
                            <label style={{ 
                                display: 'flex', 
                                alignItems: 'flex-end', 
                                cursor: 'pointer', 
                                fontSize: '14px' 
                            }}>
                                <input
                                    type="checkbox"
                                    checked={agreeToPolicies}
                                    onChange={(e) => setAgreeToPolicies(e.target.checked)}
                                    style={{ 
                                        marginRight: '10px', 
                                        marginTop: '2px', 
                                        transform: 'scale(1.2)' 
                                    }}
                                    required
                                />
                                <span>
                                    By proceeding with the Reservation you agree to the{' '}
                                    <button
                                        type="button"
                                        onClick={() => setShowPoliciesDialog(true)}
                                        style={{
                                            color: '#4f8cff',
                                            background: 'none',
                                            border: 'none',
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            padding: '0',
                                            font: 'inherit'
                                        }}
                                    >
                                        Resort Policies
                                    </button>
                                    .
                                </span>
                            </label>
                        </div>

                        {/* Deposit Requirement Agreement Section */}
                        <div style={{ 
                            marginTop: 16, 
                            padding: '15px', 
                            border: '1px solid #ddd', 
                            borderRadius: '8px', 
                            backgroundColor: '#f9f9f9' 
                        }}>
                            <label style={{ 
                                display: 'flex', 
                                alignItems: 'flex-start', 
                                cursor: 'pointer', 
                                fontSize: '14px' 
                            }}>
                                <input
                                    type="checkbox"
                                    checked={agreeToDeposit}
                                    onChange={(e) => setAgreeToDeposit(e.target.checked)}
                                    style={{ 
                                        marginRight: '10px', 
                                        marginTop: '2px', 
                                        transform: 'scale(1.2)' 
                                    }}
                                    required
                                />
                                <span>
                                    I understand that a 50% deposit is required to confirm my reservation and that it will not be guaranteed until payment is received.
                                </span>
                            </label>
                        </div>

                        {/* Additional Charges Agreement Section */}
                        <div style={{ 
                            marginTop: 16, 
                            padding: '15px', 
                            border: '1px solid #ddd', 
                            borderRadius: '8px', 
                            backgroundColor: '#f9f9f9' 
                        }}>
                            <label style={{ 
                                display: 'flex', 
                                alignItems: 'flex-start', 
                                cursor: 'pointer', 
                                fontSize: '14px' 
                            }}>
                                <input
                                    type="checkbox"
                                    checked={agreeToCharges}
                                    onChange={(e) => setAgreeToCharges(e.target.checked)}
                                    style={{ 
                                        marginRight: '10px', 
                                        marginTop: '2px', 
                                        transform: 'scale(1.2)' 
                                    }}
                                    required
                                />
                                <span>
                                    I understand that having more persons will be subject to an additional charge of <span style={{ fontWeight: 'bold', color: 'rgb(79, 140, 255)' }}>Php1,200.00</span> per head.
                                </span>
                            </label>
                        </div>
                        
                        <div style={{ 
                            marginTop: 20, 
                            display: 'flex', 
                            flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                            justifyContent: window.innerWidth <= 768 ? 'stretch' : 'flex-end', 
                            gap: window.innerWidth <= 768 ? 12 : 12,
                            paddingTop: 16,
                            borderTop: '1px solid #e3f0ff'
                        }}>
                            <button 
                                onClick={handleDialogCancel} 
                                disabled={isSubmitting}
                                style={{
                                    background: '#6c757d',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    padding: window.innerWidth <= 768 ? '14px 20px' : '10px 20px',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                    fontWeight: 500,
                                    fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                                    order: window.innerWidth <= 768 ? 2 : 1
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={
                                    (isSubmitting || !guestName.trim() || !guestEmail.trim() || !guestContact.trim() || !agreeToPolicies || !agreeToDeposit || !agreeToCharges) 
                                        ? handleDisabledButtonClick 
                                        : handleDialogProceed
                                }
                                style={{
                                    background: (!guestName.trim() || !guestEmail.trim() || !guestContact.trim() || isSubmitting || !agreeToPolicies || !agreeToDeposit || !agreeToCharges) ? '#bbb' : 'linear-gradient(90deg, #4f8cff 0%, #6ec6ff 100%)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    padding: window.innerWidth <= 768 ? '14px 20px' : '10px 20px',
                                    cursor: (!guestName.trim() || !guestEmail.trim() || !guestContact.trim() || isSubmitting || !agreeToPolicies || !agreeToDeposit || !agreeToCharges) ? 'not-allowed' : 'pointer',
                                    fontWeight: 600,
                                    fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                                    order: window.innerWidth <= 768 ? 1 : 2
                                }}
                            >
                                {isSubmitting ? 'Processing...' : 'Proceed with Reservation'}
                            </button>
                        </div>
                    </div>
                </div>, 
                document.body
            )}

            {/* Mobile Wizard Dialog */}
            {showDialog && selectedRoom && isMobileWizard && createPortal(
                <div style={{
                    position: 'fixed', 
                    top: 0, 
                    left: 0, 
                    width: '100vw', 
                    height: '100vh',
                    height: '100dvh', // Dynamic viewport height for mobile browsers
                    background: 'rgba(0,0,0,0.8)', 
                    display: 'flex', 
                    alignItems: 'stretch', 
                    justifyContent: 'center', 
                    zIndex: 9999,
                    padding: 0,
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                }}>
                    <div style={{ 
                        background: '#fff', 
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        fontFamily: 'Segoe UI, Arial, sans-serif',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Header with navigation */}
                        <div style={{
                            background: 'linear-gradient(90deg, #4f8cff 0%, #6ec6ff 100%)',
                            color: '#fff',
                            padding: '20px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            <button 
                                onClick={wizardStep === 1 ? exitWizard : previousWizardStep}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#fff',
                                    fontSize: '18px',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                ← {wizardStep === 1 ? 'Cancel' : 'Back'}
                            </button>
                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    {wizardStep === 1 && 'Room & Pricing'}
                                    {wizardStep === 2 && 'Guest Information'}
                                    {wizardStep === 3 && 'Confirmation'}
                                </div>
                                <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '2px' }}>
                                    Step {wizardStep} of 3
                                </div>
                            </div>
                            <div style={{ width: '60px' }}></div> {/* Spacer for alignment */}
                        </div>

                        {/* Progress Bar */}
                        <div style={{
                            background: '#f0f0f0',
                            height: '4px'
                        }}>
                            <div style={{
                                background: 'linear-gradient(90deg, #4f8cff 0%, #6ec6ff 100%)',
                                height: '100%',
                                width: `${(wizardStep / 3) * 100}%`,
                                transition: 'width 0.3s ease'
                            }}></div>
                        </div>

                        {/* Content Area */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '20px 16px'
                        }}>
                            {/* Step 1: Room & Pricing */}
                            {wizardStep === 1 && (
                                <div>
                                    <h2 style={{ 
                                        color: '#4f8cff', 
                                        margin: '0 0 16px 0',
                                        fontSize: '20px'
                                    }}>
                                        {selectedRoom.name}
                                    </h2>
                                    
                                    {/* Booking Summary */}
                                    <div style={{
                                        background: '#f7fbff',
                                        border: '1.5px solid #e3f0ff',
                                        borderRadius: 8,
                                        padding: 16,
                                        marginBottom: 16
                                    }}>
                                        <h4 style={{ margin: '0 0 12px 0', color: '#2a3b5c' }}>Booking Details</h4>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.95em', marginBottom: 12 }}>
                                            <div><strong>Adults:</strong> {adults}</div>
                                            <div><strong>Kids:</strong> {kids}</div>
                                            <div><strong>Check-in:</strong> {checkInDate}</div>
                                            <div><strong>Check-out:</strong> {checkOutDate}</div>
                                        </div>
                                    </div>

                                    {/* Voucher Application */}
                                    <VoucherApplication 
                                        onVoucherApplied={handleVoucherApplied}
                                        onVoucherRemoved={handleVoucherRemoved}
                                        appliedVoucher={appliedVoucher}
                                        bookingTotal={getBaseAmount()}
                                        roomRate={selectedRoom.price_per_night}
                                        hasAnyMandatoryDiscount={hasAnyMandatoryDiscount()}
                                        selectedRoomTypeId={selectedRoom.id}
                                        checkInDate={checkInDate}
                                    />

                                    {/* Mandatory Discounts */}
                                    <div style={{ 
                                        marginTop: 16, 
                                        padding: '15px',
                                        background: appliedVoucher ? '#f8f9fa' : '#fff9c4',
                                        border: appliedVoucher ? '2px solid #dee2e6' : '2px solid #ffc107',
                                        borderRadius: 8,
                                        opacity: appliedVoucher ? 0.6 : 1,
                                        position: 'relative'
                                    }}>
                                        {appliedVoucher && (
                                            <div style={{
                                                position: 'absolute',
                                                top: 12,
                                                right: 12,
                                                background: '#dc3545',
                                                color: '#fff',
                                                padding: '4px 8px',
                                                borderRadius: 4,
                                                fontSize: '0.8em',
                                                fontWeight: 'bold'
                                            }}>
                                                DISABLED
                                            </div>
                                        )}
                                        
                                        <MandatoryDiscountsManager
                                            discounts={mandatoryDiscounts}
                                            setDiscounts={setMandatoryDiscounts}
                                            disabled={!!appliedVoucher}
                                            showPreview={true}
                                            maxCapacity={selectedRoom?.max_capacity}
                                        />
                                    </div>

                                    {/* Cost Summary */}
                                    <div style={{
                                        marginTop: 20,
                                        padding: '16px',
                                        background: '#f8f9fa',
                                        border: '2px solid #4f8cff',
                                        borderRadius: 8
                                    }}>
                                        <h4 style={{ 
                                            margin: '0 0 12px 0', 
                                            color: '#4f8cff', 
                                            fontSize: '1.1em',
                                            textAlign: 'center'
                                        }}>
                                            💳 Payment Summary
                                        </h4>
                                        
                                        <div style={{ fontSize: '1.05em', lineHeight: '1.6' }}>
                                            {/* Base Amount */}
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                marginBottom: '8px',
                                                color: '#2a3b5c'
                                            }}>
                                                <span>Base Amount ({nights} night{nights > 1 ? 's' : ''}):</span>
                                                <span style={{ fontWeight: 'bold' }}>
                                                    ₱{getBaseAmount().toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                                </span>
                                            </div>

                                            {/* Discounts */}
                                            {(() => {
                                                let discountAmount = 0;
                                                let discountLabel = '';
                                                
                                                if (hasAnyMandatoryDiscount()) {
                                                    const breakdown = getMandatoryDiscountBreakdown();
                                                    if (breakdown) {
                                                        discountAmount = breakdown.discountAmount + breakdown.vatExemptAmount;
                                                        discountLabel = breakdown.discountCount === 1 ? 
                                                            (mandatoryDiscounts[0].type === MANDATORY_DISCOUNT_TYPES.SENIOR ? 'SC' : 'PWD') : 
                                                            `${breakdown.discountCount} Discounts`;
                                                    }
                                                } else if (appliedVoucher && appliedVoucher.discountAmount > 0) {
                                                    discountAmount = appliedVoucher.discountAmount;
                                                    discountLabel = 'Voucher';
                                                }
                                                
                                                return discountAmount > 0 ? (
                                                    <div style={{ 
                                                        display: 'flex', 
                                                        justifyContent: 'space-between', 
                                                        marginBottom: '8px',
                                                        color: '#dc3545'
                                                    }}>
                                                        <span>Discounts ({discountLabel}):</span>
                                                        <span style={{ fontWeight: 'bold' }}>
                                                            -₱{discountAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                                        </span>
                                                    </div>
                                                ) : null;
                                            })()}

                                            {/* Total Amount */}
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between',
                                                paddingTop: '12px',
                                                borderTop: '2px solid #4f8cff',
                                                fontSize: '1.2em',
                                                fontWeight: 'bold',
                                                color: '#4f8cff'
                                            }}>
                                                <span>Total Amount:</span>
                                                <span>₱{getTotalAmount().toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Spacer to ensure Continue button is visible */}
                                    <div style={{ height: '80px' }}></div>
                                </div>
                            )}

                            {/* Step 2: Guest Information */}
                            {wizardStep === 2 && (
                                <div>
                                    <h2 style={{ 
                                        color: '#4f8cff', 
                                        margin: '0 0 20px 0',
                                        fontSize: '20px'
                                    }}>
                                        Guest Information
                                    </h2>
                                    
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#2a3b5c' }}>
                                            Guest Name: <span style={{ color: '#dc3545' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={guestName}
                                            onChange={e => setGuestName(e.target.value)}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px',
                                                border: '1px solid #ddd',
                                                borderRadius: 4,
                                                fontSize: '16px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#2a3b5c' }}>
                                            Email: <span style={{ color: '#dc3545' }}>*</span>
                                        </label>
                                        <input
                                            type="email"
                                            value={guestEmail}
                                            onChange={e => setGuestEmail(e.target.value)}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px',
                                                border: '1px solid #ddd',
                                                borderRadius: 4,
                                                fontSize: '16px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#2a3b5c' }}>
                                            Mobile/Contact No.: <span style={{ color: '#dc3545' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={guestContact}
                                            onChange={e => setGuestContact(e.target.value)}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px',
                                                border: '1px solid #ddd',
                                                borderRadius: 4,
                                                fontSize: '16px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#2a3b5c' }}>
                                            Customer Notes:
                                        </label>
                                        <textarea
                                            value={customerNotes}
                                            onChange={e => setCustomerNotes(e.target.value)}
                                            placeholder="Add notes or special requests here..."
                                            style={{
                                                width: '100%',
                                                minHeight: 80,
                                                padding: '12px 16px',
                                                border: '1px solid #ddd',
                                                borderRadius: 4,
                                                fontSize: '16px',
                                                resize: 'vertical',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    {/* Spacer to ensure Continue button is visible */}
                                    <div style={{ height: '80px' }}></div>
                                </div>
                            )}

                            {/* Step 3: Confirmation */}
                            {wizardStep === 3 && (
                                <div>
                                    <h2 style={{ 
                                        color: '#4f8cff', 
                                        margin: '0 0 20px 0',
                                        fontSize: '20px'
                                    }}>
                                        Confirmation
                                    </h2>

                                    {/* Final Summary */}
                                    <div style={{
                                        background: '#f7fbff',
                                        border: '1.5px solid #e3f0ff',
                                        borderRadius: 8,
                                        padding: 16,
                                        marginBottom: 20
                                    }}>
                                        <h4 style={{ margin: '0 0 12px 0', color: '#2a3b5c' }}>Reservation Summary</h4>
                                        <div style={{ fontSize: '0.95em', lineHeight: '1.6' }}>
                                            <div><strong>Room:</strong> {selectedRoom.name}</div>
                                            <div><strong>Guest:</strong> {guestName}</div>
                                            <div><strong>Email:</strong> {guestEmail}</div>
                                            <div><strong>Contact:</strong> {guestContact}</div>
                                            <div><strong>Check-in:</strong> {checkInDate}</div>
                                            <div><strong>Check-out:</strong> {checkOutDate}</div>
                                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e3f0ff' }}>
                                                <strong style={{ color: '#4f8cff' }}>Total: ₱{getTotalAmount().toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Payment Information Note */}
                                    <div style={{ 
                                        marginBottom: 20,
                                        padding: '16px', 
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #e9ecef',
                                        borderRadius: '8px',
                                        borderLeft: '4px solid #4f8cff'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginBottom: '8px'
                                        }}>
                                            <span style={{
                                                fontSize: '18px',
                                                marginRight: '8px'
                                            }}>💡</span>
                                            <span style={{
                                                fontWeight: '600',
                                                color: '#2a3b5c',
                                                fontSize: '14px'
                                            }}>Payment Information</span>
                                        </div>
                                        <p style={{
                                            margin: 0,
                                            color: '#6c757d',
                                            fontSize: '13px',
                                            lineHeight: '1.5'
                                        }}>
                                            You may settle the full payment to secure your reservation faster, but at least 50% is required to confirm your booking. The balance can be paid upon check-in.
                                        </p>
                                    </div>

                                    {/* Agreements */}
                                    <div style={{ 
                                        marginBottom: 16, 
                                        padding: '15px', 
                                        border: '1px solid #ddd', 
                                        borderRadius: '8px', 
                                        backgroundColor: '#f9f9f9' 
                                    }}>
                                        <label style={{ 
                                            display: 'flex', 
                                            alignItems: 'flex-start', 
                                            cursor: 'pointer', 
                                            fontSize: '14px' 
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={agreeToPolicies}
                                                onChange={(e) => setAgreeToPolicies(e.target.checked)}
                                                style={{ 
                                                    marginRight: '10px', 
                                                    marginTop: '2px', 
                                                    transform: 'scale(1.2)' 
                                                }}
                                                required
                                            />
                                            <span>
                                                By proceeding with the Reservation you agree to the{' '}
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPoliciesDialog(true)}
                                                    style={{
                                                        color: '#4f8cff',
                                                        background: 'none',
                                                        border: 'none',
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer',
                                                        padding: '0',
                                                        font: 'inherit'
                                                    }}
                                                >
                                                    Resort Policies
                                                </button>
                                                .
                                            </span>
                                        </label>
                                    </div>

                                    <div style={{ 
                                        marginBottom: 16, 
                                        padding: '15px', 
                                        border: '1px solid #ddd', 
                                        borderRadius: '8px', 
                                        backgroundColor: '#f9f9f9' 
                                    }}>
                                        <label style={{ 
                                            display: 'flex', 
                                            alignItems: 'flex-start', 
                                            cursor: 'pointer', 
                                            fontSize: '14px' 
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={agreeToDeposit}
                                                onChange={(e) => setAgreeToDeposit(e.target.checked)}
                                                style={{ 
                                                    marginRight: '10px', 
                                                    marginTop: '2px', 
                                                    transform: 'scale(1.2)' 
                                                }}
                                                required
                                            />
                                            <span>
                                                I understand that a 50% deposit is required to confirm my reservation and that it will not be guaranteed until payment is received.
                                            </span>
                                        </label>
                                    </div>

                                    <div style={{ 
                                        marginBottom: 16, 
                                        padding: '15px', 
                                        border: '1px solid #ddd', 
                                        borderRadius: '8px', 
                                        backgroundColor: '#f9f9f9' 
                                    }}>
                                        <label style={{ 
                                            display: 'flex', 
                                            alignItems: 'flex-start', 
                                            cursor: 'pointer', 
                                            fontSize: '14px' 
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={agreeToCharges}
                                                onChange={(e) => setAgreeToCharges(e.target.checked)}
                                                style={{ 
                                                    marginRight: '10px', 
                                                    marginTop: '2px', 
                                                    transform: 'scale(1.2)' 
                                                }}
                                                required
                                            />
                                            <span>
                                                I understand that having more persons will be subject to an additional charge of <span style={{ fontWeight: 'bold', color: 'rgb(79, 140, 255)' }}>Php1,200.00</span> per head.
                                            </span>
                                        </label>
                                    </div>
                                    {/* Spacer to ensure Complete Reservation button is visible */}
                                    <div style={{ height: '80px' }}></div>
                                </div>
                            )}
                        </div>

                        {/* Bottom Action Buttons */}
                        <div style={{
                            padding: '16px',
                            background: '#f8f9fa',
                            borderTop: '1px solid #e3f0ff'
                        }}>
                            {wizardStep < 3 ? (
                                <button
                                    onClick={nextWizardStep}
                                    disabled={wizardStep === 2 && (!guestName.trim() || !guestEmail.trim() || !guestContact.trim())}
                                    style={{
                                        width: '100%',
                                        background: (wizardStep === 2 && (!guestName.trim() || !guestEmail.trim() || !guestContact.trim())) ? 
                                            '#bbb' : 'linear-gradient(90deg, #4f8cff 0%, #6ec6ff 100%)',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        padding: '16px 20px',
                                        cursor: (wizardStep === 2 && (!guestName.trim() || !guestEmail.trim() || !guestContact.trim())) ? 
                                            'not-allowed' : 'pointer',
                                        fontWeight: 600,
                                        fontSize: '16px'
                                    }}
                                >
                                    Continue
                                </button>
                            ) : (
                                <button
                                    onClick={
                                        (isSubmitting || !guestName.trim() || !guestEmail.trim() || !guestContact.trim() || !agreeToPolicies || !agreeToDeposit || !agreeToCharges) 
                                            ? handleDisabledButtonClick 
                                            : handleDialogProceed
                                    }
                                    disabled={isSubmitting || !guestName.trim() || !guestEmail.trim() || !guestContact.trim() || !agreeToPolicies || !agreeToDeposit || !agreeToCharges}
                                    style={{
                                        width: '100%',
                                        background: (!guestName.trim() || !guestEmail.trim() || !guestContact.trim() || isSubmitting || !agreeToPolicies || !agreeToDeposit || !agreeToCharges) ? 
                                            '#bbb' : 'linear-gradient(90deg, #4f8cff 0%, #6ec6ff 100%)',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        padding: '16px 20px',
                                        cursor: (!guestName.trim() || !guestEmail.trim() || !guestContact.trim() || isSubmitting || !agreeToPolicies || !agreeToDeposit || !agreeToCharges) ? 
                                            'not-allowed' : 'pointer',
                                        fontWeight: 600,
                                        fontSize: '16px'
                                    }}
                                >
                                    {isSubmitting ? 'Processing...' : 'Complete Reservation'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>, 
                document.body
            )}

            {/* Mobile Results Dialog */}
            {showMobileResults && rooms.length > 0 && createPortal(
                <div style={{
                    position: 'fixed', 
                    top: 0, 
                    left: 0, 
                    width: '100vw', 
                    height: '100vh',
                    height: '100dvh', // Dynamic viewport height for mobile browsers
                    background: 'rgba(0,0,0,0.8)', 
                    display: 'flex', 
                    alignItems: 'stretch', 
                    justifyContent: 'center', 
                    zIndex: 9999,
                    padding: 0,
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                }}>
                    <div style={{ 
                        background: '#fff', 
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        fontFamily: 'Segoe UI, Arial, sans-serif',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Header with back button */}
                        <div style={{
                            background: 'linear-gradient(90deg, #4f8cff 0%, #6ec6ff 100%)',
                            color: '#fff',
                            padding: '20px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            <button 
                                onClick={() => setShowMobileResults(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#fff',
                                    fontSize: '18px',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                ← Back
                            </button>
                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    Available Rooms
                                </div>
                                <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '2px' }}>
                                    {rooms.length} room{rooms.length > 1 ? 's' : ''} found
                                </div>
                            </div>
                            <div style={{ width: '60px' }}></div> {/* Spacer for alignment */}
                        </div>

                        {/* Scrollable Room List */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '0'
                        }}>
                            <RoomList rooms={rooms} onReserve={handleReserve} isMobileDialog={true} />
                            {/* Spacer to prevent last button from being obscured */}
                            <div style={{ height: '80px' }}></div>
                        </div>
                    </div>
                </div>, 
                document.body
            )}

            {/* Policies Dialog Modal */}
            {showPoliciesDialog && createPortal(
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    height: '100dvh', // Dynamic viewport height for mobile browsers
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10000,
                    padding: window.innerWidth <= 768 ? '16px' : '20px',
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: window.innerWidth <= 768 ? '8px' : '12px',
                        padding: window.innerWidth <= 768 ? '20px 16px' : '30px',
                        maxWidth: window.innerWidth <= 768 ? '100%' : '700px',
                        width: window.innerWidth <= 768 ? '100%' : 'auto',
                        maxHeight: window.innerWidth <= 768 ? 'calc(100dvh - 32px)' : '80vh', // Adjust for padding using dvh
                        overflow: 'auto',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginBottom: window.innerWidth <= 768 ? '16px' : '20px',
                            flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap'
                        }}>
                            <h2 style={{ 
                                margin: 0, 
                                color: '#2a3b5c', 
                                fontSize: window.innerWidth <= 768 ? '20px' : '24px', 
                                fontWeight: 'bold',
                                flex: 1
                            }}>RESORT POLICIES</h2>
                            <button
                                onClick={() => setShowPoliciesDialog(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#666',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        
                        <div style={{ lineHeight: '1.6', color: '#333' }}>
                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Check-in & Check-out:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Check-in is at 2:00 PM, and check-out is at 12:00 PM. Early check-in or late check-out may be possible with advance notice and could result in extra charges. If a guest checks out early, they will still be charged for the full amount of the original booking.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Cancellation and No-Show Policy:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Guests can cancel for free if they do so 7 or more days before their check-in date. If they cancel 5 to 6 days before check-in, a 50% cancellation fee will be charged. Cancellations made less than 5 days before check-in are non-refundable. No-shows will be charged for the full amount of all reserved nights.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Rebooking Policy:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Guests can rebook once, provided the new check-in date is within 3 months of the original date. Rebooking is free if done 7 or more days before the original check-in date, but a fee may apply if done within 7 days.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Force Majeure:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>The resort is not liable for its failure to deliver services in the event of unforeseen circumstances like natural disasters, war, or strikes.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Minors:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Guests aged 14 to 17 must be accompanied by a responsible adult at all times. Children under 14 years old are not allowed under any circumstances.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Children:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Children under 3 feet can share a bed with an adult for free. If a separate bed is needed, an extra rate will apply.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Room Occupancy:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Standard and Honeymoon Casitas can accommodate up to 2 guests, while Family Casitas can accommodate up to 4 guests. Exceeding the room capacity without prior approval is not permitted and may incur additional charges.</p>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>*Each pup tent accommodates a maximum of 2 guests and family tent accommodates a maximum of 4 guests. Additional guests will require a separate tent.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Rates:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Rates are quoted per casita, per night, in Philippine Pesos. The rate is locked in once a booking is confirmed but can change for unbooked stays.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Damage or Loss:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Guests are responsible for any damage to the casita or resort facilities and will be charged for any losses upon check-out.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Prohibited Items:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>The possession of dangerous drugs, firearms, and other deadly weapons is strictly forbidden. Violators will be asked to leave without a refund, and authorities will be notified.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Drone Use:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Flying drones is prohibited to ensure the safety and privacy of guests. Only authorized personnel are allowed to use them for marketing purposes.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Outside Food and Drinks:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Cooking and bringing outside food and drinks are strictly prohibited. Any individual found cooking will be subject to a fine of Php10,000.00, and maybe asked to leave without refund. For your convenience, we have an in-house restaurant.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Smoking:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Villas are 100% smoke-free, and guests must use designated smoking areas. Anyone caught smoking outside designated area will be fined Php5,000.00.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Pets:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Pets are not allowed within the resort premises to ensure the comfort, safety, and cleanliness of all guests. We kindly ask that guests make appropriate arrangements for their pets prior to arrival.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Usage of Swimming pool, Billiard Hall, and Roofdeck (for glamping guests only):</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Glamping guests may use the amenities (Swimming pool, Billiard Hall, and Roofdeck) between 8:00AM until 6:00PM, unless the casitas are exclusively booked for a private event. All pool rules posted in the pool area must be strictly observed at all times.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Credit Cards:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>The resort accepts Visa and Mastercard. To prevent fraud, guests may be required to present the credit card used for the booking and a valid photo ID upon check-in.</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ color: '#2a3b5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Waiver of Liability:</h3>
                                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>The resort is not responsible for any injuries, accidents, or losses to guests or their belongings. Guests use all resort facilities at their own risk. Parents are solely responsible for supervising their children.</p>
                            </div>
                        </div>

                        <div style={{ marginTop: '30px', textAlign: 'center' }}>
                            <button
                                onClick={() => setShowPoliciesDialog(false)}
                                style={{
                                    backgroundColor: '#4f8cff',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>, 
                document.body
            )}

            {showThankYou && reservationSummary && createPortal(
                <div style={{
                    position: 'fixed', 
                    top: 0, 
                    left: 0, 
                    width: '100vw', 
                    height: '100vh',
                    height: '100dvh', // Dynamic viewport height for mobile browsers
                    background: 'rgba(0,0,0,0.6)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    zIndex: 9999,
                    margin: 0,
                    padding: window.innerWidth <= 768 ? '16px' : '0',
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                }}>
                    <div
                        style={{
                            background: '#f7fbff',
                            borderRadius: window.innerWidth <= 768 ? 8 : 16,
                            padding: window.innerWidth <= 768 ? '24px 20px' : '32px 28px',
                            maxWidth: window.innerWidth <= 768 ? '100%' : 420,
                            width: window.innerWidth <= 768 ? '100%' : '90vw',
                            maxHeight: window.innerWidth <= 768 ? 'calc(100dvh - 32px)' : '90vh', // Adjust for padding using dvh
                            overflowY: 'auto',
                            boxShadow: '0 8px 32px rgba(44,62,80,0.15)',
                            fontFamily: 'Segoe UI, Arial, sans-serif',
                            color: '#2a3b5c',
                            textAlign: 'center',
                            position: 'relative'
                        }}
                    >
                        <h2 style={{ 
                            color: '#4f8cff', 
                            marginBottom: 12,
                            fontSize: window.innerWidth <= 768 ? '20px' : '24px'
                        }}>Thank you for reserving!</h2>
                        <div style={{ marginBottom: 18 }}>
                            <span style={{ 
                                color: '#357ae8', 
                                fontSize: window.innerWidth <= 768 ? '1em' : '1.15em'
                            }}>
                                Your reference number is: <br/><span style={{ 
                                    color: '#ff9900ff', 
                                    fontWeight: 700, 
                                    fontSize: window.innerWidth <= 768 ? '1.1em' : '1.15em'
                                }}>{bookingNo}</span>
                            </span>
                        </div>
                        <div style={{ marginBottom: 18 }}>
                            <span style={{ color: '#357ae8' }}>
                                Kindly send proof of payment to <span style={{ color: '#ff9900ff', fontWeight: 700 }}>{paymentSettings.adminEmail}</span><br />
                                with the subject:<br />
                                <span style={{ color: '#ff9900ff', fontWeight: 700 }}>Proof of Payment: {bookingNo}</span>
                            </span>
                        </div>
                        <div style={{
                            background: '#fff',
                            borderRadius: 10,
                            padding: '18px 12px',
                            margin: '0 auto 18px auto',
                            boxShadow: '0 2px 8px rgba(44,62,80,0.07)',
                            textAlign: 'left',
                            fontSize: '1.05em'
                        }}>
                            <div style={{ marginBottom: 8 }}>
                                <strong>Reservation details:</strong>
                            </div>
                            <div>Number of Pax: <strong>{reservationSummary.pax}</strong></div>
                            <div>Room/Suite: <strong>{reservationSummary.room}</strong></div>
                            <div>Stay: <strong>{reservationSummary.nights} night{reservationSummary.nights > 1 ? 's' : ''}</strong></div>
                            <div>Dates: <strong>{reservationSummary.dates}</strong></div>
                            
                            {/* Simple Cost Breakdown */}
                            <div style={{ 
                                marginTop: 16, 
                                paddingTop: 12, 
                                borderTop: '2px solid #4f8cff',
                                background: '#f8f9fa',
                                padding: '12px',
                                borderRadius: 6
                            }}>
                                <div style={{ marginBottom: 6, fontWeight: 'bold', color: '#4f8cff', textAlign: 'center' }}>
                                    Payment Summary
                                </div>
                                
                                {/* Base Amount */}
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    marginBottom: '6px',
                                    fontSize: '0.95em'
                                }}>
                                    <span>Base Amount (₱{(reservationSummary.baseAmount / reservationSummary.nights).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} × {reservationSummary.nights} night{reservationSummary.nights > 1 ? 's' : ''}):</span>
                                    <span style={{ fontWeight: 'bold' }}>₱{reservationSummary.baseAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>

                                {/* Discounts */}
                                {(() => {
                                    let discountLabel = '';
                                    let discountAmount = 0;
                                    
                                    if (reservationSummary.mandatoryDiscounts && reservationSummary.mandatoryDiscounts.length > 0) {
                                        if (reservationSummary.mandatoryDiscounts.length === 1) {
                                            discountLabel = reservationSummary.mandatoryDiscounts[0].type === 'SENIOR' ? 'SC' : 'PWD';
                                        } else {
                                            discountLabel = `${reservationSummary.mandatoryDiscounts.length} Discounts`;
                                        }
                                        discountAmount = (reservationSummary.mandatoryDiscountAmount || 0) + (reservationSummary.vatExemptAmount || 0);
                                    } else if (reservationSummary.voucherDiscountAmount && reservationSummary.voucherDiscountAmount > 0) {
                                        discountLabel = 'Voucher';
                                        discountAmount = reservationSummary.voucherDiscountAmount;
                                    }
                                    
                                    return discountAmount > 0 ? (
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            marginBottom: '6px',
                                            fontSize: '0.95em',
                                            color: '#dc3545'
                                        }}>
                                            <span>Discounts ({discountLabel}):</span>
                                            <span style={{ fontWeight: 'bold' }}>-₱{discountAmount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                        </div>
                                    ) : null;
                                })()}

                                {/* Total Amount */}
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    paddingTop: '8px',
                                    borderTop: '1px solid #4f8cff',
                                    fontSize: '1.1em',
                                    fontWeight: 'bold',
                                    color: '#4f8cff'
                                }}>
                                    <span>Total Amount:</span>
                                    <span>₱{Number(reservationSummary.total).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>
                            </div>
                        </div>
                        <div style={{
                            background: '#eaf4ff',
                            borderRadius: 10,
                            padding: '14px 10px',
                            margin: '0 auto 18px auto',
                            fontSize: '1.05em',
                            textAlign: 'left'
                        }}>
                            <div style={{ marginBottom: 6 }}><strong>Payment channels:</strong></div>
                            {paymentSettings.paymentChannels.map((channel, index) => (
                                <div key={channel.id || index}>
                                    {channel.name}: <strong>{channel.value}</strong>
                                    <br />
                                    <span style={{ fontSize: '0.9em', color: '#666' }}>Account Name: {channel.account_name}</span>
                                </div>
                            ))}
                        </div>
                        <div style={{ margin: '18px 0 10px 0', color: '#2a3b5c', fontWeight: 500 }}>
                            Have a nice day!
                        </div>
                        <div style={{ color: '#4f8cff', fontWeight: 700, fontSize: '1.1em' }}>
                            - La Casita Beach Resort
                        </div>
                        <button
                            style={{
                                marginTop: 24,
                                padding: '10px 28px',
                                background: '#4f8cff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 8,
                                fontSize: '1.1em',
                                fontWeight: 600,
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(44,62,80,0.10)'
                            }}
                            onClick={() => {
                                setShowThankYou(false);
                                setAdults(1);
                                setKids(0);
                                setCheckInDate('');
                                setNights(1);
                                setRooms([]);
                                setGuestName('');
                                setGuestEmail('');
                                setGuestContact('');
                                setCustomerNotes('');
                                setBookingNo('');
                                setReservationSummary(null);
                                setAppliedVoucher(null);
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>, 
                document.body
            )}
        </>
    );
};

export default RoomSearch;

// Add this to the top or in your CSS file for the slideDown animation:
// @keyframes slideDown {
//   from { top: 0; opacity: 0; }
//   to { top: 24px; opacity: 1; }
// }